export type LensRoutableMessage = {
  id: string;
  is_user: boolean;
  kind?: "CHAT" | "ARTF" | "QUESTION" | "ANSWER" | "EDIT";
  parent_message_id?: string | null;
  thinking_method_id?: string | null;
};

const normalizeLensId = (value: string | null | undefined): string | null => {
  const id = typeof value === "string" ? value.trim() : "";
  return id || null;
};

export const getAssistantLensId = (
  message: LensRoutableMessage | null | undefined,
): string | null => {
  if (!message || message.is_user) return null;
  return normalizeLensId(message.thinking_method_id);
};

export function resolveRegenerationLensId({
  editedMessage,
  messages,
  activePath,
  selectedDocumentLensId,
  conversationPrimaryLensId,
}: {
  editedMessage: LensRoutableMessage | null | undefined;
  messages: LensRoutableMessage[];
  activePath: LensRoutableMessage[];
  selectedDocumentLensId?: string | null;
  conversationPrimaryLensId?: string | null;
}): string | null {
  const selectedDocumentLens = normalizeLensId(selectedDocumentLensId);
  const primaryLens = normalizeLensId(conversationPrimaryLensId);
  if (!editedMessage?.id) return selectedDocumentLens ?? primaryLens;

  const allMessages = [...messages, ...activePath];
  const directAssistantChild = allMessages.find(
    (message) =>
      !message.is_user &&
      message.parent_message_id === editedMessage.id &&
      !!normalizeLensId(message.thinking_method_id),
  );
  const directChildLens = getAssistantLensId(directAssistantChild);
  if (directChildLens) return directChildLens;

  const messageById = new Map<string, LensRoutableMessage>();
  for (const message of allMessages) {
    messageById.set(message.id, message);
  }

  const seen = new Set<string>();
  let parentId = editedMessage.parent_message_id ?? null;
  let hops = 0;
  while (parentId && !seen.has(parentId) && hops < 24) {
    seen.add(parentId);
    const parent = messageById.get(parentId);
    if (!parent) break;

    if (!parent.is_user && (parent.kind === "QUESTION" || parent.kind === "ARTF")) {
      const parentLens = getAssistantLensId(parent);
      if (parentLens) return parentLens;
    }

    parentId = parent.parent_message_id ?? null;
    hops += 1;
  }

  return selectedDocumentLens ?? primaryLens;
}
